import {
  ArrowLeftIcon,
  Database01Icon,
  Edit01Icon,
  PlayCircleIcon,
} from '@langchain/untitled-ui-icons';

import { useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';

import { EvaluatorFeedbackSchema } from '@/components/EvaluatorCrudPane/EvaluatorFeedbackSchema';
import { useHubTypeManifest } from '@/components/EvaluatorCrudPane/utils/useHubTypeManifest';
import { ResourceDetailLayout } from '@/components/ResourceDetailTabLayout/ResourceDetailTabLayout';
import {
  ResourceActionsSlot,
  ResourceTabsSlot,
} from '@/components/ResourceDetailTabLayout/ResourceTabLayoutPortals';
import { TabGroup } from '@/components/Tabs/Tabs';
import {
  Button,
  IconButton,
  TabLabel as TabLabelV2,
  TabList as TabListV2,
  TabPanel as TabPanelV2,
  TabPanels as TabPanelsV2,
} from '@/design-system';
import { isEvaluatorVersionAlignable } from '@/hooks/useCanAlignEvaluators';
import { useDataset, useOrganizationId } from '@/hooks/useSwr';
import { ResourceType, RuleSchema } from '@/types/schema';
import { appDatasetsPath, appOrganizationPath } from '@/utils/constants';

import { useEvaluatorStore } from '../../components/EvaluatorCrudPane/store/evaluatorStore';
import { getNonRunnableBindingModelFromManifest } from '../Playground/utils/Playground.utils';
import { AnnotationQueueSection } from './components/AnnotationQueueSection';
import { CollapsibleSection } from './components/CollapsibleSection';
import { EvaluatorDetailsCodeView } from './components/EvaluatorDetailsCodeView';
import { PromptSection } from './components/PromptSection';
import { TracesTab } from './components/TracesTab';

export const EvaluatorDetailsPage = (props: {
  evaluator: RuleSchema;
  setEditModalOpen: (open: boolean) => void;
}) => {
  const organizationId = useOrganizationId();
  const { datasetId } = useParams();

  const storedModel = useEvaluatorStore((state) => state.model);
  const { manifest } = useHubTypeManifest();
  const evaluatorVersion = useEvaluatorStore((state) => state.evaluatorVersion);
  const model = useMemo(() => {
    return evaluatorVersion >= 3
      ? getNonRunnableBindingModelFromManifest(manifest) ?? storedModel
      : storedModel;
  }, [manifest, storedModel, evaluatorVersion]);

  const handleEditClick = () => {
    props.setEditModalOpen(true);
  };

  const isLLMAsJudge = useMemo(() => {
    return props.evaluator.evaluators && props.evaluator.evaluators.length > 0;
  }, [props.evaluator]);

  const isCodeEvaluator = useMemo(() => {
    return !!props.evaluator.code_evaluators?.[0];
  }, [props.evaluator]);

  const areEvaluatorsAlignable = isEvaluatorVersionAlignable(evaluatorVersion);

  const isEvaluatorDisabled =
    !props.evaluator.is_enabled &&
    props.evaluator.created_at === props.evaluator.updated_at &&
    props.evaluator.alignment_annotation_queue_id != null;

  const mainPageContent = useMemo(() => {
    if (isLLMAsJudge) {
      return (
        <>
          {areEvaluatorsAlignable && (
            <AnnotationQueueSection
              alignmentQueueId={props.evaluator.alignment_annotation_queue_id}
              datasetId={props.evaluator.dataset_id}
            />
          )}
          <CollapsibleSection title="Feedback Config" defaultOpen={true}>
            <EvaluatorFeedbackSchema
              evaluatedEntityType="dataset-eval"
              readOnly={true}
              hideDescriptionAndControls={true}
            />
          </CollapsibleSection>

          <CollapsibleSection title="Evaluator Prompt" defaultOpen={true}>
            {props.evaluator.evaluators && (
              <PromptSection
                evaluatedEntityType="dataset-eval"
                model={model}
                variableMapping={
                  props.evaluator.evaluators[0].structured?.variable_mapping ??
                  null
                }
                isEvaluatorDisabled={isEvaluatorDisabled}
              />
            )}
          </CollapsibleSection>
        </>
      );
    } else if (isCodeEvaluator) {
      return (
        <CollapsibleSection title="Evaluator Code" defaultOpen={true}>
          <EvaluatorDetailsCodeView
            evaluator_code={props.evaluator.code_evaluators![0].code}
          />
        </CollapsibleSection>
      );
    }

    return null;
  }, [
    props.evaluator,
    isLLMAsJudge,
    isCodeEvaluator,
    model,
    areEvaluatorsAlignable,
    isEvaluatorDisabled,
  ]);

  const pageTitleProps = useMemo(
    () => ({
      title: props.evaluator.display_name || 'Unnamed Evaluator',
      id: props.evaluator.id,
      type: ResourceType.Evaluator,
      leftDecorator: (
        <Link
          className="flex size-4 items-center"
          to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}?tab=2`}
        >
          <IconButton
            label="Back"
            icon={ArrowLeftIcon}
            color="secondary"
            className="-ml-2"
            size="sm"
            variant="plain"
          />
        </Link>
      ),
    }),
    [
      props.evaluator.display_name,
      props.evaluator.id,
      organizationId,
      datasetId,
    ]
  );

  const correctionsDataset = useDataset(
    props.evaluator.corrections_dataset_id ?? undefined
  );
  const correctionsDatasetHasExamples =
    (correctionsDataset?.data?.example_count ?? 0) > 0;

  return (
    <ResourceDetailLayout pageTitleProps={pageTitleProps} hideBreadcrumbs>
      <TabGroup>
        <ResourceTabsSlot.Fill>
          <TabListV2>
            <TabLabelV2 label="Overview" />
            <TabLabelV2 label="Evaluator Traces" />
          </TabListV2>
        </ResourceTabsSlot.Fill>

        <TabPanelsV2 className="flex flex-grow flex-col overflow-hidden overflow-y-auto bg-secondary">
          <TabPanelV2 className="h-full overflow-auto bg-secondary p-6">
            <div className="space-y-4">{mainPageContent}</div>
          </TabPanelV2>

          <TabPanelV2 className="flex flex-grow bg-primary">
            <TracesTab evaluatorId={props.evaluator.id} />
          </TabPanelV2>
        </TabPanelsV2>
      </TabGroup>

      <ResourceActionsSlot.Fill>
        <div className="flex items-center gap-2">
          {props.evaluator.corrections_dataset_id && (
            <Link
              to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${props.evaluator.corrections_dataset_id}`}
            >
              <Button
                variant="outlined"
                color="secondary"
                size="md"
                leftDecorator={Database01Icon}
              >
                {areEvaluatorsAlignable
                  ? 'Reference Dataset'
                  : 'Correction Dataset'}
              </Button>
            </Link>
          )}
          {areEvaluatorsAlignable &&
            props.evaluator.alignment_annotation_queue_id &&
            correctionsDatasetHasExamples && (
              <Link
                to={`/${appOrganizationPath}/${organizationId}/${appDatasetsPath}/${datasetId}/evaluator/${props.evaluator.id}/playground`}
              >
                <Button
                  type="button"
                  variant="normal"
                  color="primary"
                  leftDecorator={PlayCircleIcon}
                  size="md"
                >
                  Evaluator Playground
                </Button>
              </Link>
            )}
          <IconButton
            icon={Edit01Icon}
            label="Edit evaluator"
            onClick={handleEditClick}
            color="primary"
            variant="outlined"
            size="md"
          />
        </div>
      </ResourceActionsSlot.Fill>
    </ResourceDetailLayout>
  );
};
